// Блокировка приложения на устройстве: следит за временем «не на экране», держит сессию
// открытой, пока приложение на экране, и закрывает всё экраном разблокировки.

import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { authApi, authKeys, type AuthState } from '@/api/auth'
import { setActiveIdentity } from '@/api/identity'
import { errorMessage } from '@/api/client'
import { useLogout } from '@/api/useLogout'
import { Button, ErrorText, Icon, Input } from '@/components/ui'
import { useNetworkStatus } from '@/lib/online'
import { AuthLayout } from '@/pages/auth/parts'

import { afterUnlock } from './afterUnlock'
import * as store from './store'
import {
  handleLocked,
  prefetchUnlockOptions,
  submitPendingUnlock,
  UnlockError,
  unlockWithPasskey,
  unlockWithPassword,
} from './unlock'

const ACTIVE_MARK_MS = 15_000
const HEARTBEAT_MS = 30_000

function useLockController(state: AuthState | undefined) {
  const client = useQueryClient()
  const { locked, lockAfter } = store.useLockState()

  // Сервер — источник правды о блокировке сессии этого устройства.
  const serverLockAfter = state?.lock_after_s
  const serverLocked = state?.locked
  useEffect(() => {
    if (serverLockAfter !== undefined) store.setLockAfter(serverLockAfter)
  }, [serverLockAfter])
  useEffect(() => {
    if (serverLocked) handleLocked(() => afterUnlock(client))
  }, [serverLocked, client])

  // Время «на экране» и блокировка после возврата.
  useEffect(() => {
    if (lockAfter === null || locked) return
    const check = () => {
      if (document.visibilityState === 'hidden') {
        store.markActive()
        return
      }
      if (store.isOverdue()) store.setLocked(true)
      else store.markActive()
    }
    check()
    document.addEventListener('visibilitychange', check)
    window.addEventListener('pageshow', check)
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') store.markActive()
    }, ACTIVE_MARK_MS)
    return () => {
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('pageshow', check)
      clearInterval(timer)
    }
  }, [lockAfter, locked])

  // Пока приложение на экране — продлеваем сессию на сервере и держим вызов для офлайна.
  useEffect(() => {
    if (lockAfter === null || locked) return
    const beat = async () => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return
      if (store.getPendingUnlock() && !(await submitPendingUnlock())) return
      try {
        const fresh = await authApi.state()
        client.setQueryData(authKeys.state, fresh)
      } catch {
        return
      }
      void prefetchUnlockOptions()
    }
    void beat()
    const timer = setInterval(() => void beat(), HEARTBEAT_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void beat()
    }
    window.addEventListener('online', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(timer)
      window.removeEventListener('online', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [lockAfter, locked, client])

  return locked
}

/** Показывает детей, пока приложение не заблокировано; иначе — экран разблокировки. */
export function LockGate({ state, children }: { state: AuthState | undefined; children: ReactNode }) {
  // Без мигания данных: просроченное время проверяем до первой отрисовки.
  useState(() => {
    if (store.isOverdue()) store.setLocked(true)
    return null
  })
  const user = state?.user
  const full = Boolean(state?.authenticated && user && !user.requires_email && !user.requires_totp_setup)
  const restricted = Boolean(state?.authenticated && user && (user.requires_email || user.requires_totp_setup))
  const locked = useLockController(user === undefined ? state : full ? state : undefined)
  const identityKey = state?.user === undefined ? 'unknown' : full && user ? `user:${user.id}:${user.legacy_owner}` : 'none'
  const [identityReadyKey, setIdentityReadyKey] = useState(state?.user === undefined ? 'unknown' : '')
  useEffect(() => {
    if (state?.user === undefined) return
    let current = true
    void setActiveIdentity(full && user ? { id: user.id, legacy_owner: user.legacy_owner } : null).finally(() => {
      if (current) setIdentityReadyKey(identityKey)
    })
    return () => { current = false }
  }, [full, identityKey, state?.user, user])
  const identityReady = identityReadyKey === identityKey
  if (!identityReady) return <div className="grid min-h-dvh place-items-center" aria-label="Подготовка личных данных"><span className="sr-only">Загрузка</span></div>
  if (restricted) return <Navigate to="/account/complete" replace />
  if (state?.authenticated && state.user === null) return <Navigate to="/login" replace />
  return locked ? <LockScreen /> : <>{children}</>
}

function unlockErrorText(error: unknown): string | null {
  if (!error) return null
  return error instanceof UnlockError ? error.message : errorMessage(error)
}

export function LockScreen() {
  const client = useQueryClient()
  const online = useNetworkStatus()
  const { passkeyId } = store.useLockState()
  const canPasskey = Boolean(passkeyId) && browserSupportsWebAuthn()
  const logout = useLogout()

  const [password, setPassword] = useState('')
  const [pending, setPending] = useState<'passkey' | 'password' | null>(null)
  const [error, setError] = useState<unknown>(null)
  const autoTried = useRef(false)

  const run = async (kind: 'passkey' | 'password', action: () => Promise<unknown>) => {
    setPending(kind)
    setError(null)
    try {
      await action()
      afterUnlock(client)
    } catch (e) {
      setError(e)
      if (kind === 'password') setPassword('')
    } finally {
      setPending(null)
    }
  }

  const passkey = () => void run('passkey', unlockWithPasskey)

  // Android и десктоп разрешают запрос отпечатка без нажатия; iOS — нет (там нужна кнопка).
  useEffect(() => {
    if (!canPasskey || /iPhone|iPad|Macintosh/.test(navigator.userAgent)) return
    const timer = setTimeout(() => {
      if (autoTried.current || document.visibilityState !== 'visible') return
      autoTried.current = true
      passkey()
    }, 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPasskey])

  const submitPassword = (e: FormEvent) => {
    e.preventDefault()
    if (password) void run('password', () => unlockWithPassword(password))
  }

  return (
    <AuthLayout
      title="Приложение заблокировано"
      description={canPasskey ? 'Приложи палец или введи пароль.' : 'Введи пароль, чтобы продолжить.'}
    >
      <div className="flex flex-col gap-5">
        {canPasskey ? (
          <Button
            variant="primary"
            size="lg"
            block
            loading={pending === 'passkey'}
            disabled={pending !== null}
            onClick={passkey}
          >
            <Icon name="fingerprint" size={22} />
            Разблокировать отпечатком
          </Button>
        ) : null}
        {online ? (
          <form onSubmit={submitPassword} className="flex flex-col gap-4" noValidate>
            <input type="text" name="username" autoComplete="username" value="owner" readOnly hidden />
            <Input
              label="Пароль"
              type="password"
              autoComplete="current-password"
              autoFocus={!canPasskey}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              variant={canPasskey ? 'secondary' : 'primary'}
              size="lg"
              block
              loading={pending === 'password'}
              disabled={!password || pending !== null}
            >
              Разблокировать паролем
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted">Нет сети — паролем разблокировать не получится.</p>
        )}
        <ErrorText>{unlockErrorText(error)}</ErrorText>
        <Button variant="ghost" size="sm" onClick={() => logout.mutate()} loading={logout.isPending}>
          Выйти
        </Button>
      </div>
    </AuthLayout>
  )
}
